import { useEffect, useRef } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { useMediaQuery } from "react-responsive";

import * as css from "styles/CssVariables.js";
import globalConstants from "constants/globalConstants";

import ButtonRoundIcon from "components/buttons/ButtonRoundIcon";
import Dropdown from "components/forms/Dropdown";
import DropdownSelection from "components/forms/DropdownSelection";
import SelectionItem from "components/forms/SelectionItem";

import landingPagesIcon from "assets/img/icons/icons8-macbook.svg";
import leftIcon from "assets/img/icons/left-arrow.svg";
import rightIcon from "assets/img/icons/right-arrow.svg";
import leftWhiteIcon from "assets/img/icons/left-arrow-white.svg";
import rightWhiteIcon from "assets/img/icons/right-arrow-white.svg";
import rightUpArrowIcon from "assets/img/icons/icons8-right-up-arrow.svg";
import rightUpArrowWhiteIcon from "assets/img/icons/icons8-right-up-arrow-white.svg";
import { PAGES_DOMAIN_URL } from "constants";

const TemplateList = styled.div`
  position: relative;
  width: 100%;
  padding: ${css.BOX_PADDING_PRIMARY};
  box-sizing: border-box;

  background: ${css.BLUE_BOX_GRADIENT};
  border-radius: ${css.BOX_RADIUS_PRIMARY};

  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN_LARGE};
`;

const TopContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN};
`;

const BoxTitle = styled.div`
  width: 100%;

  line-height: 130%;

  font-size: ${css.BOX_TITLE_FONT_SIZE};
  font-weight: ${css.BOX_TITLE_FONT_WEIGHT};
`;

const TitleIcon = styled.div`
  display: inline-block;
  padding-right: 6px;

  transform: translateY(4.5px);

  width: 26px;
  min-width: 26px;
`;

const Text = styled.div`
  line-height: 130%;
`;

const Filters = styled.div`
  margin-top: ${css.VERTICAL_BOX_MARGIN};
`;

const DropdownWrap = styled.div`
  position: relative;
  width: 100%;
  max-width: 290px;

  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN};
`;

const SelectionText = styled.div``;

const Template = styled.div`
  width: 100%;

  transition: 0.2s;

  display: flex;
  justify-content: space-between;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN};
`;

const BoxHeadWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const BoxHeader = styled.div`
  width: 100%;

  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const PageName = styled.div`
  padding-top: 4px;
  padding-bottom: 7px;

  font-size: ${css.BOX_TITLE_FONT_SIZE};
  font-weight: ${css.BOX_TITLE_FONT_WEIGHT};
  overflow-wrap: anywhere;

  @media (max-width: ${globalConstants.switchMobile}) {
    padding-top: 0px;
  }
`;

const OpenIconWrap = styled.div`
  position: absolute;
  top: 15px;
  right: 15px;
  z-index: 10;
`;

const Pagination = styled.div`
  width: 100%;
  margin-top: ${css.VERTICAL_BOX_MARGIN};

  display: flex;
  justify-content: center;
  gap: ${css.BUTTONS_GAP_SMALL};

  @media (max-width: ${globalConstants.switchTablet}) {
    margin-top: 6px;
    margin-bottom: ${css.VERTICAL_BOX_MARGIN};
  }
`;

const PageScreenshot = styled.div`
  position: relative;
  width: 100%;
`;

const PageImage = styled.div`
  width: 100%;

  border-radius: ${css.BOX_RADIUS_SECONDARY};

  overflow: hidden;
`;

export default ({
  data,
  categorySelectionOpened,
  toggleCategoryForm,
  categorySelected,
  onCategorySelect,
  onOpenClick,
  limit,
  page,
  paginate,
}) => {
  const categoryDropdownRef = useRef();
  const categorySelectionRef = useRef();

  function useCategorySelectionOutsideClick(ref) {
    useEffect(() => {
      function handleClickOutside(event) {
        if (
          ref.current &&
          !ref.current.contains(event.target) &&
          !categoryDropdownRef.current.contains(event.target)
        ) {
          toggleCategoryForm();
        }
      }

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [ref]);
  }

  useCategorySelectionOutsideClick(categorySelectionRef);

  const tabletScreen = useMediaQuery({
    query: `(max-width: ${globalConstants.switchTablet})`,
  });

  const numOfResults = data.pageTemplateListData.numOfResults;

  const list = data.pageTemplateListData.pages_templates.map((template) => {
    const { id, name } = template;

    const screenshot = name.replaceAll(" ", "_");

    return (
      <Template key={id}>
        <BoxHeadWrapper>
          <BoxHeader>
            <PageName>{name}</PageName>
          </BoxHeader>
        </BoxHeadWrapper>
        <PageScreenshot>
          <OpenIconWrap>
            <ButtonRoundIcon
              icon={rightUpArrowIcon}
              hoverIcon={rightUpArrowWhiteIcon}
              background={css.BRAND_YELLOW}
              hoverBackground={css.BRAND_GREEN}
              onClick={() => onOpenClick(id)}
              buttonSize="32px"
              iconSize="14px"
            />
          </OpenIconWrap>
          <PageImage>
            <img
              src={`/assets-public/img/page-template-screenshots/${screenshot}.jpg`}
              style={{ width: "100%" }}
            />
          </PageImage>
        </PageScreenshot>
      </Template>
    );
  });

  // prepare pagination
  let showPagination = false;

  if (numOfResults > limit) showPagination = true;

  const maxPage = Math.ceil(numOfResults / limit);

  // prepare category selection
  const categoryOptions = {
    all: {
      name: "All categories",
    },
    homeBasedBusiness: {
      name: "Home Based Business",
    },
    travel: {
      name: "Travel",
    },
    finance: {
      name: "Finance",
    },
    makeMoneyOnline: {
      name: "Make Money Online",
    },
    cosmetics: {
      name: "Cosmetics",
    },
    skinCare: {
      name: "Skin Care",
    },
    crypto: {
      name: "Crypto",
    },
  };

  let categoryOptionsList = [];

  Object.keys(categoryOptions).forEach((category) => {
    categoryOptionsList.push(
      <SelectionItem onClick={() => onCategorySelect(category)} key={category}>
        <SelectionText>{categoryOptions[category].name}</SelectionText>
      </SelectionItem>
    );
  });

  return (
    <TemplateList>
      <TopContent>
        <BoxTitle>
          <TitleIcon>
            <img src={landingPagesIcon} style={{ width: "100%" }} />
          </TitleIcon>
          Choose a Landing Page Template
        </BoxTitle>
        <Text>
          Select a template from which to create your new landing page. Click on
          the yellow icon to open the page. If you like it, you can create your
          own customized page from it.
        </Text>
        <Filters>
          <DropdownWrap>
            <Dropdown
              onClick={toggleCategoryForm}
              dropdownRef={categoryDropdownRef}
            >
              <SelectionText>
                {categoryOptions[categorySelected].name}
              </SelectionText>
            </Dropdown>
            {categorySelectionOpened && (
              <DropdownSelection
                width="290px"
                top="48px"
                selectionRef={categorySelectionRef}
                maxHeight="220px"
              >
                {categoryOptionsList}
              </DropdownSelection>
            )}
          </DropdownWrap>
        </Filters>
      </TopContent>
      {list}
      {showPagination && (
        <Pagination>
          {page > 1 && (
            <ButtonRoundIcon
              icon={leftIcon}
              hoverIcon={leftWhiteIcon}
              background={css.BRAND_YELLOW}
              hoverBackground={css.BRAND_GREEN}
              iconSize="16px"
              buttonSize="32px"
              onClick={() => paginate("previous")}
            />
          )}
          {page < maxPage && (
            <ButtonRoundIcon
              icon={rightIcon}
              hoverIcon={rightWhiteIcon}
              background={css.BRAND_YELLOW}
              hoverBackground={css.BRAND_GREEN}
              iconSize="16px"
              buttonSize="32px"
              onClick={() => paginate("next")}
            />
          )}
        </Pagination>
      )}
    </TemplateList>
  );
};
